/**
 * /redeem page — user enters invite code to activate beta access.
 * Called when getMe() returns beta_required error.
 */

import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { redeemCode } from './api.js';

interface RedeemProps {
  onSuccess: () => void;
}

export const Redeem = ({ onSuccess }: RedeemProps): ReactElement => {
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setError(null);

      try {
        const result = await redeemCode(code);
        if (result.ok) {
          setSuccess(true);
          // Give user a moment to see success message, then refresh
          setTimeout(() => {
            onSuccess();
          }, 1500);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(`Error: ${String(err)}`);
      } finally {
        setLoading(false);
      }
    },
    [code, onSuccess],
  );

  if (success) {
    return (
      <main style={{ fontFamily: 'system-ui, sans-serif', padding: 16, maxWidth: 600 }}>
        <h1>Beta Access Activated</h1>
        <p style={{ color: '#28a745', fontSize: 16 }}>Your invite code has been redeemed!</p>
        <p>Redirecting you to the game...</p>
      </main>
    );
  }

  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', padding: 16, maxWidth: 600 }}>
      <h1>Redeem Invite Code</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>
        Wonders is in closed beta. Enter your invite code to get started.
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="code-input" style={{ marginBottom: 4, fontWeight: 500 }}>
            Invite Code
          </label>
          <input
            id="code-input"
            type="text"
            placeholder="e.g., XK4P-9MZB-VQ7N-RDWF"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={loading}
            style={{
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid #ccc',
              borderRadius: 4,
              fontFamily: 'monospace',
            }}
          />
        </div>

        {error && (
          <div
            style={{
              padding: 12,
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !code.trim()}
          style={{
            padding: '10px 16px',
            fontSize: 14,
            fontWeight: 500,
            background: loading ? '#ccc' : '#a36a30',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: loading || !code.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Validating...' : 'Redeem Code'}
        </button>
      </form>

      <div
        style={{
          marginTop: 32,
          paddingTop: 24,
          borderTop: '1px solid #eee',
          fontSize: 13,
          color: '#666',
        }}
      >
        <p>Don't have an invite code? Contact the team to request early access.</p>
      </div>
    </main>
  );
};
